import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Typography, Divider, Button, TextField, MenuItem, Grid,
  FormControlLabel, Checkbox, FormGroup, CircularProgress, Chip,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import * as eqpApi from '../../lib/api/eqp';

// ISO-8601 요일번호(1=월 ~ 7=일) - 백엔드 EqpStdTimeService.parseWorkDays와 맞춘다
const WEEKDAY_OPTIONS = [
  { value: 1, label: '월' }, { value: 2, label: '화' }, { value: 3, label: '수' },
  { value: 4, label: '목' }, { value: 5, label: '금' }, { value: 6, label: '토' }, { value: 7, label: '일' },
];
const WEEKDAY_DEFAULT = '1,2,3,4,5';

const MODE_OPTIONS = [
  { value: 'COMPANY', label: '회사 기본값 사용' },
  { value: 'CUSTOM', label: '개별 설정' },
  { value: 'ALWAYS_ON', label: '24시간 무인가동' },
];

const parseWorkDays = (csv) => (csv || '').split(',').map((s) => s.trim()).filter(Boolean).map(Number);

/**
 * 설비 기준시간(계획 가동시간) 설정 — 장비 상세의 "기준정보" 탭 안에 들어가는 독립 섹션.
 * EQP_INF 저장 폼(formik)과 별개 API(GET/PUT/DELETE /api/v1/eqp/{eqpUuid}/std-time)를 쓴다 -
 * 장비가 아직 없는(신규 등록) 상태에서는 저장할 UUID가 없어 렌더하지 않는다(호출자 쪽에서 분기).
 */
const EqpStdTimeSection = ({ eqpUuid, canEdit = true }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isOverride, setIsOverride] = useState(false);
  const [mode, setMode] = useState('COMPANY');
  const [startTm, setStartTm] = useState('09:00');
  const [endTm, setEndTm] = useState('18:00');
  const [endNextDayYn, setEndNextDayYn] = useState('N');
  const [breakMin, setBreakMin] = useState(60);
  const [workDays, setWorkDays] = useState(WEEKDAY_DEFAULT);
  const [applyHolidayYn, setApplyHolidayYn] = useState('Y');

  const applyFromServer = (info) => {
    if (!info) return;
    setIsOverride(Boolean(info.isOverride));
    setMode(info.mode || 'COMPANY');
    setStartTm(info.startTm || '09:00');
    setEndTm(info.endTm || '18:00');
    setEndNextDayYn(info.endNextDayYn || 'N');
    setBreakMin(info.breakMin ?? 60);
    setWorkDays(info.workDays || WEEKDAY_DEFAULT);
    setApplyHolidayYn(info.applyHolidayYn || 'Y');
  };

  const load = useCallback(async () => {
    if (!eqpUuid) return;
    setLoading(true);
    try {
      const res = await eqpApi.getStdTime(eqpUuid);
      applyFromServer(res?.data?.info);
    } catch (e) {
      enqueueSnackbar('기준시간 설정 조회 실패', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [eqpUuid]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const toggleWeekday = (day) => {
    const days = parseWorkDays(workDays);
    const next = days.includes(day) ? days.filter((d) => d !== day) : [...days, day];
    setWorkDays(next.sort((a, b) => a - b).join(','));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (mode === 'COMPANY') {
        // 회사 기본값 사용 = 덮어쓰기 삭제(개별 설정 안 함)
        const res = await eqpApi.revertStdTime(eqpUuid);
        applyFromServer(res?.data?.info);
      } else {
        const res = await eqpApi.saveStdTime(eqpUuid, {
          mode, startTm, endTm, endNextDayYn, breakMin: Number(breakMin) || 0, workDays, applyHolidayYn,
        });
        applyFromServer(res?.data?.info);
      }
      enqueueSnackbar('기준시간 설정을 저장했습니다.', { variant: 'success' });
    } catch (e) {
      enqueueSnackbar(e?.response?.data?.message || '기준시간 설정 저장 실패', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (!eqpUuid) return null;

  const selectedDays = parseWorkDays(workDays);

  return (
    <Box>
      <Divider sx={{ my: 0.5 }}>
        <Typography variant="caption" color="text.secondary">기준시간 (계획 가동시간)</Typography>
      </Divider>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={20} /></Box>
      ) : (
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary">
              가동률 계산의 분모(기준시간)를 정합니다. 회사 기본값은 근무유형(평일 09:00~18:00, 휴게 60분)
              − 공휴일 기준입니다.
              {!isOverride && <Chip size="small" sx={{ ml: 1 }} label="회사 기본값 사용 중" />}
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              select fullWidth label="기준시간 방식" size="small"
              value={mode}
              onChange={(e) => setMode(e.target.value)}
              disabled={!canEdit}
            >
              {MODE_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </TextField>
          </Grid>

          {mode === 'CUSTOM' && (
            <>
              <Grid item xs={6} sm={3}>
                <TextField
                  fullWidth label="시작 시간" size="small" type="time"
                  InputLabelProps={{ shrink: true }}
                  value={startTm}
                  onChange={(e) => setStartTm(e.target.value)}
                  disabled={!canEdit}
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField
                  fullWidth label="종료 시간" size="small" type="time"
                  InputLabelProps={{ shrink: true }}
                  value={endTm}
                  onChange={(e) => setEndTm(e.target.value)}
                  disabled={!canEdit}
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={endNextDayYn === 'Y'}
                      onChange={(e) => setEndNextDayYn(e.target.checked ? 'Y' : 'N')}
                      disabled={!canEdit}
                    />
                  }
                  label="익일 종료(야간조)"
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField
                  fullWidth label="휴게시간(분)" size="small" type="number"
                  value={breakMin}
                  onChange={(e) => setBreakMin(e.target.value)}
                  disabled={!canEdit}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">근무 요일</Typography>
                <FormGroup row>
                  {WEEKDAY_OPTIONS.map((d) => (
                    <FormControlLabel
                      key={d.value}
                      control={
                        <Checkbox
                          size="small"
                          checked={selectedDays.includes(d.value)}
                          onChange={() => toggleWeekday(d.value)}
                          disabled={!canEdit}
                        />
                      }
                      label={d.label}
                    />
                  ))}
                </FormGroup>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={applyHolidayYn === 'Y'}
                      onChange={(e) => setApplyHolidayYn(e.target.checked ? 'Y' : 'N')}
                      disabled={!canEdit}
                    />
                  }
                  label="공휴일에는 기준시간 0으로 처리"
                />
              </Grid>
            </>
          )}

          {mode === 'ALWAYS_ON' && (
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary">
                매일 24시간을 기준시간으로 계산합니다(무인 야간가공 등). 공휴일에도 24시간이 적용됩니다.
              </Typography>
            </Grid>
          )}

          {canEdit && (
            <Grid item xs={12}>
              <Button variant="outlined" size="small" onClick={handleSave} disabled={saving}>
                {saving ? '저장 중...' : '기준시간 저장'}
              </Button>
            </Grid>
          )}
        </Grid>
      )}
    </Box>
  );
};

export default EqpStdTimeSection;
